// Rascunho persistido do modal "Nova viagem".
//
// Objetivo: se o usuário começa a preencher o wizard e fecha o modal sem
// querer (clique fora, Esc, botão voltar do celular), não perde o que
// digitou. Salvamos os campos da etapa Básico + a escolha de roteiro no
// localStorage enquanto ele edita, e restauramos silenciosamente ao reabrir.
//
// O que NÃO é salvo: blobs de import (JSON da IA), presets carregados, cota,
// flags transitórias (submitting/error). Esses são pesados, sensíveis a
// versão, ou só fazem sentido na sessão atual.
//
// Limpamos o rascunho quando a viagem é criada com sucesso.

const STORAGE_KEY = 'tpv:newTripDraft:v1';

// Campos do STATE que vale a pena persistir (todos serializáveis e
// "conteúdo do usuário", não estado de UI derivado).
const PERSISTED_FIELDS = [
  'title',
  'datesUndecided',
  'start',
  'end',
  'numDays',
  'places',
  'adults',
  'children',
  'departureCity',
  'aiNotes',
  'alreadyHave',
  'routeMode',
  'aiFlow',
];

// Rascunho velho demais é descartado (provavelmente outra viagem). 7 dias.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function safeStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // localStorage pode lançar (modo privado, bloqueio de cookies).
    return null;
  }
}

/** Há algum conteúdo "de verdade" pra salvar? Evita gravar rascunho vazio. */
function hasContent(state) {
  if (!state) return false;
  return Boolean(
    (state.title && state.title.trim())
    || (state.departureCity && state.departureCity.trim())
    || (Array.isArray(state.places) && state.places.length > 0)
    || (state.aiNotes && state.aiNotes.trim())
    || (state.alreadyHave && state.alreadyHave.trim())
    || state.start || state.end,
  );
}

/** Salva (ou apaga, se vazio) o rascunho do STATE atual. */
export function persistNewTripDraft(state) {
  const store = safeStorage();
  if (!store) return;
  if (!hasContent(state)) { clearNewTripDraft(); return; }
  const draft = { savedAt: Date.now() };
  for (const k of PERSISTED_FIELDS) draft[k] = state[k];
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // quota cheia / serialização — ignora, é best-effort.
  }
}

/** Carrega o rascunho salvo (ou null se não houver / expirado / inválido). */
export function loadNewTripDraft() {
  const store = safeStorage();
  if (!store) return null;
  let raw;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let draft;
  try {
    draft = JSON.parse(raw);
  } catch {
    clearNewTripDraft();
    return null;
  }
  if (!draft || typeof draft !== 'object') return null;
  if (draft.savedAt && (Date.now() - draft.savedAt) > MAX_AGE_MS) {
    clearNewTripDraft();
    return null;
  }
  // Saneamento leve: places sempre array.
  if (!Array.isArray(draft.places)) draft.places = [];
  return draft;
}

/** Remove o rascunho. Chamado ao criar a viagem com sucesso. */
export function clearNewTripDraft() {
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // ignora
  }
}

/** Aplica um rascunho carregado por cima do STATE inicial (in place). */
export function applyNewTripDraft(state, draft) {
  if (!state || !draft) return state;
  for (const k of PERSISTED_FIELDS) {
    if (draft[k] === undefined) continue;
    state[k] = draft[k];
  }
  if (!Array.isArray(state.places)) state.places = [];
  return state;
}
